// Creature provenance on the client (shared/provenance.ts): the catcher's name and the honours a creature carries,
// as one compact line of marks for the hover card, the creature page and market listings. War Medals have their own
// pill (client/rollpills.ts), so this line shows the catcher, Trophies and the Boss Ladder best.
import type { CreatureDTO } from '../shared/protocol.ts';
import { TOUR_TIERS, isTier } from '../shared/tournament.ts';
import { ladderName, stepLevel } from '../shared/ladder.ts';
import { ICON } from './icons.ts';
import { esc, num } from './fmt.ts';

const day = (ts: number) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** The catcher as a small name mark (its date on hover); empty when unknown. */
export function catcherMark(c: Pick<CreatureDTO, 'catcher' | 'caughtAt'>): string {
  return c.catcher ? `<span class="pv-by" data-catcher="${esc(c.catcher)}" title="Caught by ${esc(c.catcher)} · ${day(c.caughtAt)}">${ICON.creatures}${esc(c.catcher)}</span>` : '';
}

/** Trophy and Boss Ladder marks; empty when the creature has neither. */
export function honourMarks(c: Pick<CreatureDTO, 'trophies' | 'ladderBest'>): string {
  const out: string[] = [];
  const tr = c.trophies ?? [];
  if (tr.length) {
    const tip = tr.map((t) => `${isTier(t.tier) ? TOUR_TIERS[t.tier].name : 'Tournament'} Champion · ${day(t.at)}`).join('\n');
    out.push(`<span class="pv-h pv-trophy" data-honour="trophy" title="${esc(tip)}" aria-label="${tr.length} ${tr.length > 1 ? 'Trophies' : 'Trophy'}">${ICON.trophy}${tr.length > 1 ? `<b>${tr.length}</b>` : ''}</span>`);
  }
  if (c.ladderBest) out.push(`<span class="pv-h pv-ladder" data-honour="ladder" title="Boss Ladder best · ${esc(ladderName(c.ladderBest))}" aria-label="Boss Ladder best ${esc(ladderName(c.ladderBest))}">${ICON.bosses}<b>${num(stepLevel(c.ladderBest))}</b></span>`);
  return out.join('');
}

/** Catcher and honours together as one small line; empty when there is nothing to show. */
export function provenanceLine(c: CreatureDTO): string {
  const inner = catcherMark(c) + honourMarks(c);
  return inner ? `<span class="pv">${inner}</span>` : '';
}
